import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { base_url } from 'Backend/api';
import { addressListFromApi } from 'utils/addressListFromApi';
import LocationPicker from './LocationPicker';
import NotificationsSidebar from './NotificationsSidebar';
import { listenNotifications } from 'firebase';
import toast from 'react-hot-toast';

const HOME_PATHS = new Set(["/", "/restaurant-home-page", "/market-home-page"]);

function buildSessionScopedHintKey(prefix, token) {
  if (!token) return "";
  const compactToken = `${String(token).slice(0, 12)}_${String(token).slice(-12)}`;
  return `${prefix}_${compactToken}`;
}

function toFiniteNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function parseMapLocation(rawValue) {
  if (!rawValue) return null;

  if (typeof rawValue === "string") {
    const [lat, lng] = rawValue.split(",").map((item) => toFiniteNumber(String(item).trim()));
    if (lat != null && lng != null) return { lat, lng };
  }

  if (typeof rawValue === "object") {
    const lat =
      toFiniteNumber(rawValue.lat) ??
      toFiniteNumber(rawValue.latitude);
    const lng =
      toFiniteNumber(rawValue.lng) ??
      toFiniteNumber(rawValue.longitude);
    if (lat != null && lng != null) return { lat, lng };
  }

  return null;
}

function getAddressCoordinates(address) {
  if (!address) return null;

  const lat =
    toFiniteNumber(address.lat) ??
    toFiniteNumber(address.latitude) ??
    toFiniteNumber(address.map_latitude) ??
    toFiniteNumber(address.address_latitude);
  const lng =
    toFiniteNumber(address.lng) ??
    toFiniteNumber(address.longitude) ??
    toFiniteNumber(address.map_longitude) ??
    toFiniteNumber(address.address_longitude);

  if (lat != null && lng != null) return { lat, lng };

  return (
    parseMapLocation(address.map_location) ||
    parseMapLocation(address.location) ||
    parseMapLocation(address.coordinates)
  );
}

function haversineDistanceKm(lat1, lng1, lat2, lng2) {
  const toRadians = (degree) => (degree * Math.PI) / 180;
  const earthRadiusKm = 6371;
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusKm * c;
}

const ResponsiveTopNavigation = () => {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const [cartCount, setCartCount] = useState(0);
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [activeAddress, setActiveAddress] = useState("");
  const [activeAddressData, setActiveAddressData] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [isAddressDropdownOpen, setIsAddressDropdownOpen] = useState(false);
  const [deliveryRadiusKm, setDeliveryRadiusKm] = useState(10);
  const [isFarAway, setIsFarAway] = useState(false);
  const [pendingDistanceHintKey, setPendingDistanceHintKey] = useState("");
  const [distanceHintDismissed, setDistanceHintDismissed] = useState(false);
  const [showCurrencyHint, setShowCurrencyHint] = useState(false);
  const [currencyHintSeen, setCurrencyHintSeen] = useState(false);
  const previousActiveAddressKeyRef = useRef("");
  const currencyHintTimeoutRef = useRef(null);
  const authToken = localStorage.getItem("token") || "";
  const distanceHintDismissKey = buildSessionScopedHintKey("distance_hint_dismissed", authToken);
  const currencyHintSeenKey = buildSessionScopedHintKey("currency_hint_seen", authToken);

  const isHomePage = HOME_PATHS.has(location.pathname);
  const activeAddressHintKey = String(
    activeAddressData?.slug || activeAddressData?.id || activeAddress || ""
  );
  const showDistanceHint =
    isHomePage &&
    isFarAway &&
    !!activeAddressHintKey &&
    !distanceHintDismissed &&
    pendingDistanceHintKey === activeAddressHintKey;

  const menuItems = [
    { id: 'home', label: 'الرئيسية', icon: 'Home', path: '/' },
    { id: 'orders', label: 'طلباتي', icon: 'ShoppingBag', path: '/orders' },
    { id: 'cart', label: 'السلة', icon: 'ShoppingCart', path: '/cart' },
    { id: 'favorites', label: 'المفضلة', icon: 'Heart', path: '/favorites' },
    { id: 'account', label: 'الحساب', icon: 'User', path: '/account' }
  ];

  const handleMenuItemClick = (path) => {
    setIsMobileMenuOpen(false);
    if (path) {
      navigate(path);
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const fetchCartCount = async () => {
    try {
      const token = localStorage.getItem("token");

      const response = await fetch(`${base_url}/api/cart`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        }
      });

      const data = await response.json();

      if (data.status) {
        setCartCount(data.data.summary.items_count || 0);
      }

    } catch (error) {
      console.error("Cart count error", error);
    }
  };

  useEffect(() => {
    fetchCartCount();
  }, []);

  const fetchDeliveryRadius = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${base_url}/api/settings/delivery-radius`, {
        headers: {
          Accept: "application/json",
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });

      if (!response.ok) return;

      const data = await response.json().catch(() => ({}));
      const meters = Number(data?.data?.delivery_radius_meters);
      if (Number.isFinite(meters) && meters > 0) {
        setDeliveryRadiusKm(meters / 1000);
      }
    } catch (error) {
      console.error("delivery radius error", error);
    }
  }, []);

  useEffect(() => {
    fetchDeliveryRadius();
  }, [fetchDeliveryRadius]);

  useEffect(() => {
    if (!distanceHintDismissKey) {
      setDistanceHintDismissed(false);
      return;
    }

    setDistanceHintDismissed(
      localStorage.getItem(distanceHintDismissKey) === "1"
    );
  }, [distanceHintDismissKey]);

  useEffect(() => {
    if (!currencyHintSeenKey) {
      setCurrencyHintSeen(false);
      setShowCurrencyHint(false);
      return;
    }

    const alreadySeen = localStorage.getItem(currencyHintSeenKey) === "1";
    setCurrencyHintSeen(alreadySeen);
    setShowCurrencyHint(false);
  }, [currencyHintSeenKey]);

  useEffect(() => {
    const updateCart = () => {
      fetchCartCount();
    };

    window.addEventListener("cartUpdated", updateCart);

    return () => {
      window.removeEventListener("cartUpdated", updateCart);
    };
  }, []);

  const openNotifications = () => {
    setIsNotificationOpen(true);
  };

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem("token");

      const response = await fetch(`${base_url}/api/notifications`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        }
      });

      const data = await response.json();

      if (data.status) {

        const list = data.data.data;

        const unread = list.filter(n => !n.read_at).length;

        setUnreadCount(unread);

      }

    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchNotifications();
    }, 10000);

    return () => clearInterval(interval);
  }, []);


  useEffect(() => {

    listenNotifications((payload) => {

      console.log("FCM PAYLOAD", payload);

      const title =
        payload.notification?.title ||
        payload.data?.title ||
        "إشعار جديد";

      const body =
        payload.notification?.body ||
        payload.data?.body ||
        "";

      toast.success(
        <div className="font-[cairo]">
          <strong>{title}</strong>
          <p>{body}</p>
        </div>
      );

      setUnreadCount(prev => prev + 1);

    });

  }, []);


  //   const fetchActiveAddress = async () => {
  //   try {
  //     const token = localStorage.getItem("token");

  //     const response = await fetch(`${base_url}/api/addresses`, {
  //       headers: {
  //         Authorization: `Bearer ${token}`,
  //         Accept: "application/json"
  //       }
  //     });

  //     const data = await response.json();

  //     if (data.status) {

  //       const active = data.data.find(addr => addr.is_active);
  //       if (active) {
  //         setActiveAddress(active.description);
  //       } else {
  //         setActiveAddress("لم يتم تحديد موقع بعد");
  //       }
  //     }

  //   } catch (error) {
  //     console.error("Error fetching active address:", error);
  //     setActiveAddress("لم يتم تحديد موقع بعد");
  //   }
  // };
  const fetchAddresses = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");

      const response = await fetch(`${base_url}/api/addresses`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        }
      });

      const data = await response.json();

      if (!data.status) return;

      const list = addressListFromApi(data.data);
      setAddresses(list);

      const active = list.find((addr) => !!addr.is_active);
      setActiveAddress(active?.description?.trim() ? active.description : "");
      setActiveAddressData(active || null);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses, location.pathname]);

  useEffect(() => {
    const onAddressesUpdated = () => {
      fetchAddresses();
    };
    window.addEventListener("addressesUpdated", onAddressesUpdated);
    return () => window.removeEventListener("addressesUpdated", onAddressesUpdated);
  }, [fetchAddresses]);

  const setActiveAddressHandler = async (slug, description) => {
    try {

      const token = localStorage.getItem("token");

      const response = await fetch(`${base_url}/api/addresses/${slug}/set-active`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        }
      });

      const data = await response.json();

      if (data.status) {

        setActiveAddress(description);
        const nextActive = addresses.find((addr) => addr.slug === slug) || null;
        setActiveAddressData(nextActive);
        setIsAddressDropdownOpen(false);
        toast.success("تم تغيير العنوان النشط");
        fetchAddresses();

      }

    } catch (error) {
      console.log(error);
    }
  };

  const dismissDistanceHint = useCallback(() => {
    setPendingDistanceHintKey("");
    setDistanceHintDismissed(true);

    if (distanceHintDismissKey) {
      localStorage.setItem(distanceHintDismissKey, "1");
    }
  }, [distanceHintDismissKey]);

  const dismissCurrencyHint = useCallback(() => {
    setShowCurrencyHint(false);
  }, []);

  const scheduleCurrencyHint = useCallback(() => {
    if (!isHomePage || currencyHintSeen) return;

    if (currencyHintTimeoutRef.current) {
      window.clearTimeout(currencyHintTimeoutRef.current);
    }

    currencyHintTimeoutRef.current = window.setTimeout(() => {
      setShowCurrencyHint(true);
      setCurrencyHintSeen(true);

      if (currencyHintSeenKey) {
        localStorage.setItem(currencyHintSeenKey, "1");
      }

      currencyHintTimeoutRef.current = null;
    }, 5000);
  }, [currencyHintSeen, currencyHintSeenKey, isHomePage]);

  useEffect(() => {
    return () => {
      if (currencyHintTimeoutRef.current) {
        window.clearTimeout(currencyHintTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const previousKey = previousActiveAddressKeyRef.current;

    if (!previousKey) {
      if (activeAddressHintKey) {
        setPendingDistanceHintKey(activeAddressHintKey);
      }
      previousActiveAddressKeyRef.current = activeAddressHintKey;
      return;
    }

    if (activeAddressHintKey && previousKey !== activeAddressHintKey) {
      setPendingDistanceHintKey(activeAddressHintKey);
    } else if (!activeAddressHintKey) {
      setPendingDistanceHintKey("");
    }

    previousActiveAddressKeyRef.current = activeAddressHintKey;
  }, [activeAddressHintKey]);

  useEffect(() => {
    if (!activeAddressData) {
      setIsFarAway(false);
      setPendingDistanceHintKey("");
      return;
    }

    const activeCoords = getAddressCoordinates(activeAddressData);
    if (!activeCoords) {
      setIsFarAway(false);
      setPendingDistanceHintKey("");
      return;
    }

    if (!navigator.geolocation) {
      setIsFarAway(false);
      setPendingDistanceHintKey("");
      return;
    }

    let cancelled = false;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (cancelled) return;

        const distanceKm = haversineDistanceKm(
          position.coords.latitude,
          position.coords.longitude,
          activeCoords.lat,
          activeCoords.lng
        );

        const nextIsFarAway = distanceKm > deliveryRadiusKm;
        setIsFarAway(nextIsFarAway);

        if (!nextIsFarAway && pendingDistanceHintKey === activeAddressHintKey) {
          setPendingDistanceHintKey("");
        }
      },
      () => {
        if (cancelled) return;
        setIsFarAway(false);
        setPendingDistanceHintKey("");
      },
      { enableHighAccuracy: false, timeout: 12000, maximumAge: 300000 }
    );

    return () => {
      cancelled = true;
    };
  }, [activeAddressData, deliveryRadiusKm, activeAddressHintKey, pendingDistanceHintKey]);

  useEffect(() => {
    if (!showDistanceHint) return undefined;

    const handleScrollDismiss = () => {
      dismissDistanceHint();
    };

    window.addEventListener("scroll", handleScrollDismiss, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScrollDismiss);
    };
  }, [showDistanceHint, dismissDistanceHint]);

  useEffect(() => {
    if (!showCurrencyHint) return undefined;

    const handleScrollDismiss = () => {
      dismissCurrencyHint();
    };

    window.addEventListener("scroll", handleScrollDismiss, { passive: true });
    document.addEventListener("scroll", handleScrollDismiss, {
      passive: true,
      capture: true,
    });

    return () => {
      window.removeEventListener("scroll", handleScrollDismiss);
      document.removeEventListener("scroll", handleScrollDismiss, true);
    };
  }, [showCurrencyHint, dismissCurrencyHint]);

  useEffect(() => {
    if (showDistanceHint || !distanceHintDismissed) return undefined;
    if (currencyHintSeen || showCurrencyHint) return undefined;

    scheduleCurrencyHint();

    return () => {
      if (currencyHintTimeoutRef.current) {
        window.clearTimeout(currencyHintTimeoutRef.current);
        currencyHintTimeoutRef.current = null;
      }
    };
  }, [
    showDistanceHint,
    distanceHintDismissed,
    currencyHintSeen,
    showCurrencyHint,
    scheduleCurrencyHint,
  ]);

  return (
    <>

      <nav className="top-navigation" dir='ltr'>
        <div className="top-navigation-logo">
          <div className="top-navigation-logo-icon">
            {/* <Icon name="Utensils" size={24} color="var(--color-primary)" /> */}
            <img src='/assets/images/logo.jpeg' className='rounded-full' />
          </div>
          <span className="hidden md:inline font-[Cairo] text-[16px] font-semibold">
            يسلمو
          </span>
        </div>

        <div className="top-navigation-menu" dir='rtl'>
          {menuItems?.map((item) => (
            <div
              key={item?.id}
              className={`top-navigation-menu-item ${location.pathname === item?.path ? 'active' : ''
                } relative`}
              onClick={() => handleMenuItemClick(item?.path)}
            >
              {item?.label}

              {item?.id === "cart" && cartCount > 0 && (
                <span className="absolute -top-2 -left-2 bg-red-500 text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full">
                  {cartCount}
                </span>
              )}
            </div>
          ))}
        </div>

        <div className="top-navigation-actions">
          {/* <div
            className="top-navigation-location cursor-pointer relative"
            onClick={() => setIsAddressDropdownOpen(!isAddressDropdownOpen)}
          >
            <Icon name="MapPin" size={16} color="currentColor" />
            <span className='font-[cairo] line-clamp-1'>{activeAddress || "..."}</span>
          </div> */}
          <div className="relative">
            <div
              onClick={() => navigate("/my-addresses")}
              className={`flex items-center justify-between gap-2 px-3 py-1.5 rounded-full cursor-pointer transition-all bg-[#FFF4E3] border
                border-orange-500
              } w-[198px] md:w-[260px]`}
              dir="rtl"
            >
              <img className='w-4 h-4' src='/assets/images/mmap.svg' />

              <span className="flex-1 text-center text-[12px] font-medium font-[cairo] truncate">
                {activeAddress || "لم يتم تحديد اى موقع"}
              </span>

              <img className='w-5 h-5' src='/assets/images/frr.svg' />
            </div>

            {showDistanceHint && (
              <div className="absolute left-1/2 top-[calc(100%+12px)] z-[70] w-[255px] -translate-x-1/2 md:w-[300px]">
                <div className="relative rounded-[12px] bg-[#2B2B2BEB] px-4 py-3 text-white shadow-xl">
                  <div className="flex items-center justify-between gap-3" dir="rtl">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        dismissDistanceHint();
                      }}
                      className="shrink-0 text-white/95"
                      aria-label="إغلاق التنبيه"
                    >
                      <Icon name="X" size={20} />
                    </button>

                    <p className="flex-1 text-right font-[cairo] text-[12px] font-medium leading-6 md:text-[13px]">
                      متأكد من العنوان؟ يبدو أنك بعيد عنه
                    </p>
                  </div>

                  <div className="absolute -top-2 left-1/2 h-4 w-4 -translate-x-1/2 rotate-45 bg-[#3B3B3B]" />
                </div>
              </div>
            )}
          </div>


          <div
            className="top-navigation-icon-button relative cursor-pointer"
            onClick={openNotifications}
          >
            <Icon name="Bell" size={20} />

            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full font-[cairo]">
                {unreadCount}
              </span>
            )}

          </div>
          <div
            onClick={() => navigate('/account')}
            className="top-navigation-icon-button">
            <Icon name="User" size={20} color="var(--color-foreground)" />
          </div>
        </div>

        {/* Hamburger button - only visible on mobile/tablet */}
        {/* <button

          className="lg:hidden fixed top-3 right-4 z-50 w-10 h-10 rounded-lg bg-card shadow-md flex items-center justify-center cursor-pointer hover:bg-muted transition-all duration-250"
          onClick={toggleMobileMenu}
          aria-label="Toggle mobile menu"
        >
          <Icon
            name={isMobileMenuOpen ? 'X' : 'Menu'}
            size={24}
            color="var(--color-foreground)"
          />
        </button> */}
      </nav>

      {isHomePage && showCurrencyHint && (
        <div className="fixed left-1/2 top-[58px] z-[75] w-[calc(100%-24px)] max-w-md -translate-x-1/2 md:top-[66px]">
          <div className="rounded-[8px] bg-[#3D3D3D] px-3 py-2 shadow-[0_6px_16px_rgba(0,0,0,0.16)]">
            <div className="flex items-center gap-2 text-white" dir="rtl">
              <button
                type="button"
                onClick={dismissCurrencyHint}
                className="shrink-0 text-white/95"
                aria-label="إغلاق تنبيه العملة"
              >
                <Icon name="X" size={18} />
              </button>

              <p className="flex-1 text-center font-[cairo] text-[11px] font-medium leading-snug md:text-[12px]">
                تم تحديث الأسعار حسب العملة الجديدة (بعد حذف صفرين)
              </p>

              <Icon name="AlertTriangle" size={20} className="shrink-0 text-white" />
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation - Mobile Only */}
<div className="lg:hidden fixed bottom-0 left-0 w-full bg-white border-t z-50 flex justify-around items-center py-2">

  {menuItems.map((item) => {
    const isActive = (() => {

  // ✅ لو العنصر هو الهوم
  if (item.id === "home") {
    return (
      location.pathname === "/" ||
      location.pathname === "/restaurant-home-page" ||
      location.pathname === "/market-home-page" ||
      location.pathname.startsWith("/restaurant/") ||
      location.pathname.startsWith("/market/") ||
      location.pathname === "/restaurants" ||
      location.pathname === "/markets" ||
      location.pathname === "/my-addresses" ||
      location.pathname === "/add-address"
    );
  }

  // باقي العناصر عادي
  return location.pathname.startsWith(item.path);
})();
    return (
      <div
        key={item.id}
        onClick={() => handleMenuItemClick(item.path)}
        className="flex flex-col items-center justify-center text-xs font-[cairo] cursor-pointer relative"
      >
        <Icon
          name={item.icon}
          size={20}
          className={isActive ? "text-[#F93209]" : "text-gray-400"}
        />

        <span className={isActive ? "text-[#F93209]" : "text-gray-500"}>
          {item.label}
        </span>

        {/* Badge للسلة */}
        {item.id === "cart" && cartCount > 0 && (
          <span className="absolute top-0 right-4 bg-[#FF0000] text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full">
            {cartCount}
          </span>
        )}
      </div>
    );
  })}

</div>

      {isMobileMenuOpen && (
        <div className="mobile-menu-overlay">
          <div className="mobile-menu-content">
            <div className="flex items-center justify-between mb-8">
              <div className="top-navigation-logo">
                <div className="top-navigation-logo-icon">
                  <Icon name="Utensils" size={24} color="var(--color-primary)" />
                </div>
                <span className="top-navigation-logo-text">Yslamo</span>
              </div>
            </div>

            <div className="top-navigation-location mb-6">
              <Icon name="MapPin" size={16} color="currentColor" />
              <span>{activeAddress || "جارٍ التحميل..."}</span>
            </div>

            {menuItems?.map((item) => (
              <div
                key={item?.id}
                className={`mobile-menu-item ${location.pathname === item?.path ? 'active' : ''
                  }`}
                onClick={() => handleMenuItemClick(item?.path)}
              >
                <div className="flex items-center gap-3">
                  <Icon name={item?.icon} size={20} color="currentColor" />
                  <span>{item?.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <NotificationsSidebar
        isOpen={isNotificationOpen}
        onClose={() => setIsNotificationOpen(false)}
        setUnreadCount={setUnreadCount}
      />
    </>
  );
};

export default ResponsiveTopNavigation;